<template>
  <div class="our-services-wrapper">
    <section class="services-hero">
      <div class="hero-particles"></div>
      <div class="hero-gradient-overlay"></div>
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <div class="hero-content">
              <div class="hero-badge" data-aos="fade-down">
                <div class="badge-shimmer"></div>
                <v-icon small color="#f46523" class="mr-2">mdi-tools</v-icon>
                <span>Professional Services</span>
              </div>

              <h1 class="hero-title" data-aos="fade-up" data-aos-delay="100">
                Our Services
              </h1>

              <p class="hero-subtitle" data-aos="fade-up" data-aos-delay="200">
                Maintenance made easy. View our whole service portfolio!
              </p>

              <div
                class="hero-features"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <div
                  class="hero-feature"
                  v-for="(f, i) in heroFeatures"
                  :key="i"
                >
                  <v-icon color="#f46523">mdi-check-circle</v-icon>
                  <span>{{ f }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <div class="hero-wave">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28..."
            opacity=".25"
            class="wave-fill"
          />
        </svg>
      </div>
    </section>
    <section class="services-section">
      <div class="floating-elements">
        <div class="float-element float-1"></div>
        <div class="float-element float-2"></div>
        <div class="float-element float-3"></div>
      </div>
      <v-container>
        <v-row justify="center">
          <v-col cols="12">
            <div v-if="loading" class="text-center py-12">
              <v-progress-circular indeterminate color="#f46523" size="54" />
              <p class="mt-4 grey--text">Loading services...</p>
            </div>

            <div v-else-if="locationRequired" class="text-center py-12 location-required-block">
              <v-icon size="64" color="#f46523">mdi-map-marker-alert-outline</v-icon>
              <h3 class="mt-4">Location permission required</h3>
              <p class="grey--text mt-2">
                {{ locationMessage }}
              </p>
              <v-btn
                color="#f46523"
                dark
                class="mt-4"
                :loading="loading"
                :disabled="loading"
                @click="handleEnableLocationClick"
              >
                Enable Location
              </v-btn>
              <div class="location-how-to-box">
                <h4 class="location-how-to-title">
                  <v-icon small class="mr-1">mdi-information-outline</v-icon>
                  How to enable location (if the browser prompt doesn’t appear)
                </h4>
                <div class="location-how-to-steps">
                  <p class="location-how-to-browser mb-1"><strong>Chrome / Edge (desktop & Android)</strong></p>
                  <ol>
                    <li>Click the <strong>lock</strong> or <strong>info</strong> icon to the left of the address bar.</li>
                    <li>Click <strong>Site settings</strong> → set <strong>Location</strong> to <strong>Allow</strong>.</li>
                    <li>Refresh this page or click <strong>Enable Location</strong> again.</li>
                  </ol>
                  <p class="location-how-to-browser mb-1 mt-3"><strong>Safari (iPhone / Mac)</strong></p>
                  <ol>
                    <li>Settings → Safari → Location (or Safari → Settings for This Website → Location).</li>
                    <li>Set to <strong>Allow</strong> or <strong>Ask</strong>, then reload and tap Enable Location again.</li>
                  </ol>
                </div>
                <div class="location-guide-links mt-3">
                  <span class="location-guide-links-label">Open browser guide:</span>
                  <a href="https://support.google.com/chrome/answer/114662" target="_blank" rel="noopener noreferrer" class="location-guide-link">Chrome / Edge</a>
                  <span class="location-guide-sep">·</span>
                  <a href="https://support.apple.com/guide/safari/websites-ibrwe2159985/mac" target="_blank" rel="noopener noreferrer" class="location-guide-link">Safari</a>
                </div>
                <p class="location-tip mt-3 mb-0">
                  If the prompt still doesn’t appear (e.g. you blocked it before), allow location in browser settings above, or open this page in a <strong>private / incognito window</strong> and the prompt will show again.
                </p>
              </div>
            </div>

            <div v-else-if="services.length" class="services-grid">
              <div
                class="service-card"
                v-for="(service, index) in services"
                :key="service._id || index"
                :class="service.featured ? 'featured' : ''"
                data-aos="fade-up"
                :data-aos-delay="100 * (index + 1)"
              >
                <div class="service-card-glow"></div>
                <div v-if="service.ribbon" class="featured-ribbon">
                  <v-icon small class="mr-1">mdi-star</v-icon>
                  <span>{{ service.ribbon }}</span>
                </div>

                <div class="service-image-wrapper">
                  <div class="image-overlay"></div>
                  <img
                    :src="service.image"
                    :alt="service.title"
                    class="service-image"
                  />
                  <div class="service-badge" :class="service.badgeClass">
                    <v-icon small color="white" class="mr-1">{{
                      service.badgeIcon
                    }}</v-icon>
                    <span>{{ service.badgeText }}</span>
                  </div>
                </div>

                <div class="service-content">
                  <div class="service-icon">
                    <div class="icon-glow"></div>
                    <v-icon color="#f46523" size="32">{{
                      service.icon
                    }}</v-icon>
                  </div>

                  <h3 class="service-title">{{ service.title }}</h3>
                  <p class="service-description">{{ service.description }}</p>

                  <div class="service-features">
                    <div
                      class="feature-item"
                      v-for="(feature, i) in service.features"
                      :key="i"
                    >
                      <v-icon small color="#f46523">mdi-check</v-icon>
                      <span>{{ feature }}</span>
                    </div>
                  </div>
                  <v-btn
                    depressed
                    color="#f46523"
                    dark
                    style="height: 40px"
                    @click="goToSubcategory(service)"
                  >
                    <span>Book Now</span>
                    <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <v-icon size="64" color="#f46523" class="opacity-30"
                >mdi-alert-circle-outline</v-icon
              >
              <h3 class="mt-4">No services found</h3>
              <p class="grey--text mt-2">
                Unable to load service categories right now.
              </p>
            </div>
            <div class="cta-section" data-aos="zoom-in-up">
              <div class="cta-glow"></div>
              <div class="cta-content-wrapper">
                <div class="cta-icon-wrapper">
                  <div class="cta-icon-ring"></div>
                  <v-icon color="#f46523" size="64">mdi-phone-in-talk</v-icon>
                </div>

                <div class="cta-text-content">
                  <h2 class="cta-title">Need a Service Right Away?</h2>
                  <p class="cta-subtitle">
                    Our team is available 24/7 to assist you with any home
                    service emergency
                  </p>
                </div>

                <div class="cta-buttons">
                  <a href="tel:+917356449135" class="cta-phone-link">
                    <v-icon left size="30" color="#f46523"
                      >mdi-phone-in-talk</v-icon
                    >
                    <span class="cta-phone-number">+91 70344 44303</span>
                  </a>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

  </div>
</template>

<script>
import { fetchCategories, fetchServicesByCategory } from "@/services/api";
import { slugifyForUrl } from "@/utils/slug";

export default {
  name: "OurServices",
  data() {
    return {
      loading: true,
      locationRequired: false,
      locationMessage:
        "Please enable location to load nearby service categories.",
      heroFeatures: [
        "Verified Professionals",
        "24/7 Availability",
        "Quality Guaranteed",
      ],
      services: [],
    };
  },
  mounted() {
    this.initServices();
  },
  methods: {
    getKochiFallbackLocation() {
      const location = {
        latitude: 9.9312,
        longitude: 76.2673,
        allowed: true,
        error: null,
        isFallback: true,
      };
      try {
        localStorage.setItem("userLocation", JSON.stringify(location));
        sessionStorage.setItem("userLocation", JSON.stringify(location));
      } catch (e) {
        /* ignore */
      }
      return location;
    },
    handleEnableLocationClick() {
      this.requestLocationOnClick();
    },

    requestLocationOnClick() {
      this.tryRevokeLocationPermission();
      if (typeof window === "undefined") return;
      if (!window.isSecureContext && window.location.hostname !== "localhost") {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        return;
      }
      if (!("geolocation" in navigator)) {
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage = "Using Kochi location to continue.";
        this.loadServiceCategories(fallback);
        return;
      }
      this.locationMessage = "Requesting location permission...";
      this.loading = true;
      try {
        navigator.geolocation.getCurrentPosition(
          this.locationSuccessCallback,
          this.locationErrorCallback,
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
      } catch (err) {
        console.error("Location request error:", err);
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage = "Using Kochi location to continue.";
        this.loadServiceCategories(fallback);
      }
    },

    async locationSuccessCallback(position) {
      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        allowed: true,
        error: null,
      };
      localStorage.setItem("userLocation", JSON.stringify(location));
      sessionStorage.setItem("userLocation", JSON.stringify(location));
      this.locationRequired = false;
      this.locationMessage = "Please enable location to load nearby service categories.";
      await this.loadServiceCategories(location);
    },

    locationErrorCallback() {
      // If the browser denies/blocks the prompt (or it times out), fall back to Kochi coords
      // so the page still works instead of getting stuck behind the location gate.
      const fallback = this.getKochiFallbackLocation();
      this.locationRequired = false;
      this.locationMessage = "Using Kochi location to continue.";
      this.loadServiceCategories(fallback);
    },

    async isLocationPermissionBlocked() {
      try {
        if (!navigator.permissions?.query) return false;
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        return permission.state === "denied";
      } catch (error) {
        return false;
      }
    },

    tryRevokeLocationPermission() {
      try {
        if (!navigator.permissions?.query) return;
        navigator.permissions.query({ name: "geolocation" }).then((p) => {
          if (typeof p.revoke === "function") p.revoke();
        });
      } catch (e) {
        /* ignore */
      }
    },

    getLocationFromStorage() {
      try {
        const raw =
          localStorage.getItem("userLocation") ||
          sessionStorage.getItem("userLocation");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.latitude == null || parsed?.longitude == null) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    },

    getCategoryIcon(categoryName) {
      const name = String(categoryName || "").toLowerCase();
      if (name.includes("plumb")) return "mdi-pipe-wrench";
      if (name.includes("paint")) return "mdi-format-paint";
      if (name.includes("clean")) return "mdi-spray-bottle";
      if (name.includes("electric")) return "mdi-flash";
      if (name.includes("ac") || name.includes("air")) return "mdi-air-conditioner";
      return "mdi-tools";
    },

    pickRandomItems(items, count = 6) {
      const shuffled = [...items];
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, count);
    },

    buildServiceCard(category, servicesInCategory, index) {
      const randomServices = this.pickRandomItems(servicesInCategory, 6);

      return randomServices.map((item, idx) => ({
        _id: item._id,
        categoryId: category._id,
        categoryName: category.name,
        title: item.title || "Service",
        icon: this.getCategoryIcon(category.name),
        image:
          item.image?.[0] ||
          category.image ||
          "https://via.placeholder.com/800x500?text=Service",
        badgeText: category.name,
        badgeIcon: "mdi-shape",
        badgeClass: idx === 0 && index === 0 ? "featured-badge" : "",
        description: String(
          item.description?.text ||
            item.description ||
            `Explore ${category.name} services in your area.`
        ).slice(0, 180),
        features: item?.service_types?.length
          ? item.service_types.slice(0, 3).map((type) => type.name)
          : ["Service options available"],
      }));
    },

    async initServices() {
      this.locationMessage =
        "Please enable location to load nearby service categories.";

      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.loading = false;
        return;
      }

      if (!navigator.geolocation) {
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage = "Using Kochi location to continue.";
        await this.loadServiceCategories(fallback);
        return;
      }

      // Request location as soon as they enter the page so the browser modal can show.
      // Small delay so the page is interactive first (can help the prompt appear on some devices).
      this.locationMessage = "Requesting location permission...";
      this.loading = true;
      const doRequest = () => {
        navigator.geolocation.getCurrentPosition(
          this.locationSuccessCallback,
          this.locationErrorCallback,
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
      };
      setTimeout(doRequest, 150);
    },

    async loadServiceCategories(location) {
      this.loading = true;
      try {
        let categories = [];

        try {
          const categoryResponse = await fetchCategories({
            lat: location?.latitude,
            lng: location?.longitude,
          });
          categories = Array.isArray(categoryResponse?.data)
            ? categoryResponse.data
            : categoryResponse?.data?.data || [];
        } catch (locationCategoryError) {
          console.error(
            "Location-based category fetch failed, trying global categories",
            locationCategoryError
          );
        }

        if (!categories.length) {
          const fallbackResponse = await fetchCategories({});
          categories = Array.isArray(fallbackResponse?.data)
            ? fallbackResponse.data
            : fallbackResponse?.data?.data || [];
        }

        const topCategories = categories.slice(0, 5);

        if (!topCategories.length) {
          this.services = [];
          return;
        }

        const serviceResults = await Promise.all(
          topCategories.map(async (category) => {
            try {
              let response;
              try {
                response = await fetchServicesByCategory({
                  categoryId: category._id,
                  page: 1,
                  pageSize: 10,
                  lat: location?.latitude,
                  lng: location?.longitude,
                });
              } catch (locationError) {
                if (locationError.response?.status !== 500) throw locationError;
                response = await fetchServicesByCategory({
                  categoryId: category._id,
                  page: 1,
                  pageSize: 10,
                });
              }

              const services = response?.data?.data?.services || [];
              return this.buildServiceCard(category, services, 0);
            } catch (error) {
              return this.buildServiceCard(category, [], 0);
            }
          })
        );

        const combinedServices = serviceResults.flat();
        this.services = this.pickRandomItems(combinedServices, 6);
      } catch (error) {
        console.error("Failed to load services page data", error);
        this.services = [];
      } finally {
        this.loading = false;
      }
    },

    goToServiceDetails(serviceName) {
      const isUSRoute =
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us");

      this.$router.push({
        name: isUSRoute ? "ServicesDetailsUS" : "ServicesDetails",
        params: { serviceName },
      });
    },

    goToSubcategory(service) {
      const isUSRoute =
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us");
      const slug = slugifyForUrl(service.title || "");
      sessionStorage.setItem("selectedService", JSON.stringify(service));
      sessionStorage.setItem(`service_slug_${slug}`, service._id);
      this.$router.push({
        name: isUSRoute ? "ServiceDetailsUS" : "ServiceDetails",
        params: { serviceSlug: slug },
      });
    },
  },
};
</script>

<style scoped>
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-25px);
  }
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(200%) rotate(45deg);
  }
}

@keyframes glow {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.our-services-wrapper {
  background: white;
  position: relative;
  padding-top: 50px;
  overflow: hidden;
}

.services-hero {
  position: relative;
  padding: 40px 0 0;
  overflow: hidden;
}

.hero-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: radial-gradient(
      2px 2px at 20% 30%,
      rgba(244, 101, 35, 0.3),
      transparent
    ),
    radial-gradient(2px 2px at 60% 70%, rgba(244, 101, 35, 0.2), transparent),
    radial-gradient(1px 1px at 50% 50%, rgba(244, 101, 35, 0.2), transparent),
    radial-gradient(1px 1px at 80% 10%, rgba(244, 101, 35, 0.3), transparent);
  background-size: 200% 200%;
  animation: gradientShift 20s ease infinite;
}

.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    ellipse at center,
    rgba(244, 101, 35, 0.08) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border-radius: 50px;
  border: 1px solid rgba(244, 101, 35, 0.3);
  box-shadow: 0 4px 20px rgba(244, 101, 35, 0.1);
  margin-bottom: 24px;
  font-size: 13px;
  font-weight: 600;
  color: black;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  animation: shimmer 3s infinite;
}

.hero-title {
  font-size: 64px;
  font-weight: 900;
  background: #f46523;
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  line-height: 1.1;
  animation: gradientShift 8s ease infinite;
  letter-spacing: -1.5px;
}

.hero-subtitle {
  font-size: 20px;
  color: black;
  margin-bottom: 32px;
  font-weight: 400;
}

.hero-features {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.hero-feature {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(8px);
  border-radius: 50px;
  border: 1px solid rgba(244, 101, 35, 0.15);
  color: black;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.hero-feature:hover {
  background: rgba(244, 101, 35, 0.1);
  border-color: rgba(244, 101, 35, 0.3);
  transform: translateY(-2px);
}

.hero-wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
}

.hero-wave svg {
  position: relative;
  display: block;
  width: calc(100% + 1.3px);
  height: 60px;
}

.wave-fill {
  fill: #0a0a0a;
}

.services-section {
  padding: 60px 0 80px;
  position: relative;
}

.floating-elements {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
}

.float-element {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.05), transparent);
  filter: blur(50px);
}

.float-1 {
  width: 400px;
  height: 400px;
  top: 10%;
  left: -5%;
  animation: float 25s ease-in-out infinite;
}

.float-2 {
  width: 300px;
  height: 300px;
  top: 70%;
  right: -5%;
  animation: float 20s ease-in-out infinite reverse;
}

.float-3 {
  width: 250px;
  height: 250px;
  bottom: 5%;
  left: 40%;
  animation: float 22s ease-in-out infinite;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 22px;
  margin-bottom: 50px;
}

@media (min-width: 1264px) {
  .services-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 960px) and (max-width: 1263px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.service-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  border: 1px solid rgba(244, 101, 35, 0.15);
  overflow: hidden;
  transition: none;
  position: relative;
  display: flex;
  cursor: pointer;
  flex-direction: column;
}

.service-card.featured {
  border: 2px solid rgba(244, 101, 35, 0.3);
}

.featured-ribbon {
  position: absolute;
  top: 15px;
  right: -25px;
  padding: 6px 30px;
  font-size: 12px;
  transform: rotate(45deg);
  box-shadow: 0 2px 10px rgba(244, 101, 35, 0.3);
  background: #f46523;
  color: white;
  z-index: 10;
}

.service-card-glow {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.service-card:hover {
  transform: none;
  box-shadow: none;
  border-color: rgba(244, 101, 35, 0.15);
}

.service-image-wrapper {
  position: relative;
  height: 160px;
  overflow: hidden;
  flex-shrink: 0;
}

.service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: none;
}

.service-card:hover .service-image {
  transform: none;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(10, 10, 10, 0.6) 100%
  );
}

.service-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 6px 12px;
  font-size: 11px;
  box-shadow: 0 2px 8px rgba(244, 101, 35, 0.3);
  background: #f46523;
  border-radius: 5px;
  color: white;
  font-weight: 600;
  z-index: 10;
}
.service-badge.trending {
  background: #4caf50;
}
.service-badge.recommended {
  background: #2196f3;
}
.service-badge.featured-badge {
  background: #ff9800;
}

.service-content {
  padding: 18px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.service-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  margin-bottom: 12px;
  border: 1px solid rgba(244, 101, 35, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(244, 101, 35, 0.1);
  position: relative;
}

.icon-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.5), transparent 70%);
  opacity: 0.3;
  border-radius: 12px;
  filter: blur(10px);
}

.service-title {
  color: black;
  font-size: 20px;
  margin-bottom: 6px;
}

.service-description {
  color: black;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 12px;
  flex-grow: 1;
}

.service-features {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.feature-item {
  color: black;
  font-size: 13px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.feature-item v-icon {
  font-size: 16px !important;
}

.service-btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  height: 30px;
}
.service-btn:hover {
  background-color: #e05e1f !important;
  transform: translateY(-1px);
  height: 30px;
}

.cta-section {
  background: white;
  border-radius: 20px;
  padding: 30px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(244, 101, 35, 0.2);
  box-shadow: none;
}

.cta-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle at 100px 100px,
    rgba(244, 101, 35, 0.1),
    transparent 70%
  );
  animation: rotate 60s linear infinite;
  opacity: 0.5;
}

.cta-content-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  position: relative;
  z-index: 1;
}

@media (max-width: 960px) {
  .services-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
  }

  .service-image-wrapper {
    height: 146px;
  }

  .service-content {
    padding: 14px;
  }

  .service-title {
    font-size: 18px;
  }

  .service-description {
    font-size: 13px;
  }

  .cta-content-wrapper {
    flex-direction: column;
    text-align: center;
  }
}

.cta-icon-wrapper {
  position: relative;
  width: 90px;
  height: 90px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cta-icon-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(244, 101, 35, 0.4);
  border-radius: 50%;
  animation: pulse 2s infinite ease-in-out;
}

.cta-text-content {
  flex-grow: 1;
}

.cta-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
  line-height: 1.2;
  color: black;
}

.cta-subtitle {
  font-size: 16px;
  color: black;
  font-weight: 300;
}

.cta-buttons {
  display: flex;
  gap: 15px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .cta-buttons {
    flex-direction: column;
    width: 100%;
  }
  .cta-buttons .v-btn {
    width: 100%;
  }
}

.cta-primary-btn {
  font-weight: 700;
  letter-spacing: 1px;
  transition: background-color 0.3s, transform 0.3s;
}
.cta-primary-btn:hover {
  background-color: #e05e1f !important;
  transform: translateY(-1px);
}

.cta-secondary-btn {
  font-weight: 600;
  letter-spacing: 1px;
  transition: background-color 0.3s, transform 0.3s;
}
.cta-secondary-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  transform: translateY(-1px);
}
.cta-phone-link {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 20px;
  font-weight: 800;
  color: #f46523 !important;
  padding: 10px 20px;
  border-radius: 12px;
  transition: all 0.3s;
  flex-shrink: 0;
}

.cta-phone-link:hover {
  background: rgba(244, 101, 35, 0.2);
  transform: translateY(-2px);
}

.cta-phone-number {
  margin-left: 10px;
}

@media (max-width: 960px) {
  .cta-phone-link {
    width: 100%;
    margin-bottom: 15px; /* Add space above the Schedule button on mobile */
  }
}

.location-required-block .location-how-to-box {
  text-align: left;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px 20px;
  margin: 24px auto 0;
  max-width: 520px;
}
.location-how-to-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
}
.location-how-to-steps { font-size: 13px; color: #4b5563; line-height: 1.6; }
.location-how-to-steps ol { margin: 0 0 0 18px; padding: 0; }
.location-how-to-steps li { margin-bottom: 6px; }
.location-how-to-browser { font-size: 13px; color: #374151; }
.location-guide-links { font-size: 13px; padding-top: 8px; border-top: 1px solid #e5e7eb; }
.location-guide-links-label { color: #6b7280; margin-right: 8px; }
.location-guide-link { color: #f46523; font-weight: 600; text-decoration: none; }
.location-guide-link:hover { text-decoration: underline; }
.location-guide-sep { color: #9ca3af; margin: 0 6px; }
.location-tip { font-size: 13px; color: #6b7280; line-height: 1.5; max-width: 520px; margin-left: auto; margin-right: auto; }
</style>
